"use client";
import { DrupalNode } from "next-drupal";
import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Banner from "@/components/sections/Banner";
import { config } from "@/lib/config";
import PartnerInfoCard from "@/components/sections/PartnerInfoCard";
import FindOutSection from "@/components/sections/FindOutSection";

interface InvestScreenProps {
  data: DrupalNode;
}

const InvestScreen: React.FC<InvestScreenProps> = ({ data }) => {
  const {
    title = "",
    field_invest_sub_title = "",
    field_energy_financing_title = "",
    field_energy_market_title = "",
    field_energy_market_subtitle = "",
    field_investment_action_title = "",
    field_energy_financing_content = {},
    field_invest_bottom_footer = {},
    field_energy_financing_images = [],
    field_investment_action_cards = [],
    field_invest_marketplace_title = "",
    field_marketplace_upper_content = {},
    field_identity_info = "",
    field_de_risk_info = "",
    field_match_info = "",
    field_marketplace_lower_content = {},
  } = data;

  const marketPlaceSteps = [
    {
      title: field_identity_info,
    },
    {
      title: field_de_risk_info,
    },
    {
      title: field_match_info,
    },
  ];

  return (
    <>
      <div className="pt-20">
        <Banner
          title={title}
          description={field_invest_sub_title}
          isRounded
          backgroundImg="/static/images/invest-banner.webp"
        />
        {/* financing charette */}
        <div id="financing-charette">
          <div className="pt-5 pb-[136px] bg-white relative mobileMax:pb-12 mobileMax:pt-2">
            <div className="mini-container relative z-[2]">
              <motion.h2
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation mt-[82px] text-numans leading-normal text-center font-semibold bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip text-[48px] mobileMax:text-[28px] px-5 mb-[50px] mobileMax:mb-3"
              >
                {field_energy_financing_title}
              </motion.h2>
              <div className="flex items-center justify-between mobileMax:block">
                <motion.div
                  className="
                                       remove-animation-fluctuation
                                       w-[50%] h-[350px]
                                       mobileMax:w-full mobileMax:h-full
                                       overflow-hidden
                                       rounded-[52px]
                                       will-change-transform
                                    "
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0 }}
                >
                  <div className="w-full h-full transform transition-transform duration-700 hover:scale-105">
                    <Image
                      src={`${config.apiBase}${field_energy_financing_images[0]?.uri?.url}`}
                      alt="grid img1"
                      height={300}
                      width={300}
                      unoptimized
                      className="w-full h-full object-cover rounded-[52px] backface-hidden"
                    />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: field_energy_financing_content.field_content?.value,
                  }}
                  className="remove-animation-fluctuation mobileMax:w-full --font-poppins text-medium mb-5 mt-3 leading-8 text-[#003350] mobileMax:mb-5 mobileMax:text-xsmall mobileMax:leading-normal w-[45%]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* three steps */}
        <div
          id="market-place"
          className="pt-4 pb-[60px] bg-[#003350] betweenMobileTab:pb-16 mobileMax:pb-6"
        >
          <div className="mini-container">
            <motion.h3
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation mt-[82px] text-numans desktop:mb-[25px] desktop:leading-[85px] text-center font-semibold bg-[#48DBB2] text-clip text-[48px] leading-[60px] mb-[50px] mobileMax:text-[28px] mobileMax:leading-normal mobileMax:mb-3"
            >
              {field_invest_marketplace_title}
            </motion.h3>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation --font-poppins text-center text-medium mb-5 mt-3 leading-8 text-[#fff] mobileMax:mb-5 mobileMax:text-small mobileMax:leading-normal"
              dangerouslySetInnerHTML={{
                __html: field_marketplace_upper_content?.value,
              }}
            />
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation flex items-center justify-center my-16 laptopMax:flex-col"
            >
              {marketPlaceSteps.map(
                (item: { title: string }, index, array: any) => {
                  const isLastIndex = index === array.length - 1;
                  return (
                    <div
                      key={index}
                      className={`remove-animation-fluctuation bg-[#48DBB2] rounded-full w-[220px] h-[220px] p-2 relative commonBoxShadow`}
                    >
                      <div className="h-full w-full flex flex-col items-center justify-center bg-white rounded-full">
                        <h5 className="mb-2 text-numans uppercase bg-[#003350] text-clip leading-normal text-odd mobileMax:text-medium w-[55%] mobileMax:w-[80%] text-center webkit-box line-clamp-1">
                          {item.title}
                        </h5>
                      </div>
                    </div>
                  );
                },
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="my-8 remove-animation-fluctuation"
            >
              <div
                className="text-list --font-poppins text-left text-medium mt-2.5 leading-normal text-[#fff] mobileMax:mb-5 mobileMax:text-small mobileMax:leading-normal"
                dangerouslySetInnerHTML={{
                  __html: field_marketplace_lower_content?.value,
                }}
              />
            </motion.div>
          </div>
        </div>
        {/* Investment in action */}
        <div
          id="investment-in-action"
          className="py-14 bg-white betweenMobileTab:py-12 mobileMax:pt-10 mobileMax:pb-6"
        >
          <div className="mini-container">
            <motion.h3
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation text-numans desktop:mb-[100px] desktop:leading-[85px] text-center font-semibold bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip text-[48px] leading-[60px] mb-[50px] mobileMax:text-[28px] mobileMax:leading-normal mobileMax:mb-8"
            >
              {field_investment_action_title}
            </motion.h3>
            <div className="flex items-center flex-wrap pb-[25px] mobileMax:block transparent-cards-border invest-cards">
              {field_investment_action_cards?.map(
                (investmentActionCard: DrupalNode, index: number) => {
                  return (
                    <motion.div
                      key={index}
                      className="remove-animation-fluctuation px-[15px] w-[50%] finacial-card-alternate-border mobileMax:w-full"
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                    >
                      <PartnerInfoCard
                        isTransparentCard={true}
                        img={`${config.apiBase}${investmentActionCard.field_image_icon.uri.url}`}
                        title={investmentActionCard?.title}
                        buttonText={investmentActionCard?.field_button_text}
                        link={investmentActionCard?.field_link}
                      />
                      <div className="alternate-bottom-border" />
                    </motion.div>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </div>
      <FindOutSection data={field_invest_bottom_footer} />
    </>
  );
};

export default InvestScreen;
